import {
  Button,
  DefaultPageSkeleton,
  FormRow,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Title
} from '@efast_public/fjd-component-library';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../shared/utils/notification/useNotification';
import { ApiRoutes, mandantClient } from '../../api/mandant-api';
import {IUserRoleDto, UpdateProfileRequest, UserPageResponse} from '../../api/mandant.types';
import { RouteKey } from '../AppRoutes';
import DeleteUsersAndRolesManagementDeleteDialog
  from "../../modals/UsersAndRolesManagementDeleteDialogModal";
import {useState} from "react";

interface EditProfileFormData {
  salutation?: string;
  titleField?: string;
  organisation?: string;
  firstName?: string;
  lastName?: string;
}


export function EditUserPage() {
  const { t } = useTranslation('mc');
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { notifyUserFromRequest } = useNotification();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: users, isLoading }: {data?: UserPageResponse, isLoading: boolean} = useQuery<UserPageResponse, Error, UserPageResponse>({
    queryKey: [ApiRoutes.users()],
    enabled: !!userId
  });
  const user = users?.content?.filter(({ id }) => id === userId)?.at(0);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty }
  } = useForm<EditProfileFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: user
      ? {
          salutation: '',
          titleField: '',
          organisation: '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? ''
        }
      : undefined
  });

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: (data: UpdateProfileRequest) =>
      notifyUserFromRequest(
        () => mandantClient.put(ApiRoutes.user(userId ?? ''), data),
        {
          successMessage: t('api.success.updateProfile'),
          loadingMessage: t('api.loading.updateProfile'),
          errorMessage: t('api.error.updateProfile')
        }
      )
  });

  const onSubmit = (data: EditProfileFormData) => {
    updateProfile({
      salutation: data.salutation ?? '',
      title: data.titleField ?? '',
      organisation: data.organisation ?? '',
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      username: ''
    });
  };

  const salutationOptions = [
    { label: t('addUserPage.salutation.mr'), value: 'mr' },
    { label: t('addUserPage.salutation.mrs'), value: 'mrs' },
    { label: t('addUserPage.salutation.diverse'), value: 'diverse' }
  ];

  const titleOptions = [
    { label: t('addUserPage.title_field.dr'), value: 'dr' },
    { label: t('addUserPage.title_field.prof'), value: 'prof' },
    { label: t('addUserPage.title_field.profDr'), value: 'profDr' }
  ];

  if (isLoading) {
    return (
      <DefaultPageSkeleton />
    );
  }

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '40px 20px' }}>
      <Stack direction="column" spacing="l">
        <Title level={1} visualLevel={3}>
          {t('editProfilePage.title')}
        </Title>

        <form>
          <Stack direction="column" spacing="m">
              <Text strong size="m">
                {t('editProfilePage.sectionPersonal')}
              </Text>

              <FormRow label={t('editProfilePage.email')}>
                <Stack direction="row" spacing="s" alignItems="center">
                  <Text size="m">{user?.email ?? ''}</Text>
                  {user?.emailVerified && (
                    <Text
                      size="s"
                      style={{ color: 'var(--fjd2-color-success)' }}
                    >
                      {t('editProfilePage.emailVerified')}
                    </Text>
                  )}
                </Stack>
              </FormRow>

              <FormRow
                label={t('editProfilePage.salutation')}
                validationMessage={errors.salutation?.message}
                variant={errors.salutation ? 'error' : undefined}
              >
                <Controller
                  name="salutation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      data-testid="salutation-select"
                      disabled
                      options={salutationOptions}
                      selectedOptions={
                        field.value
                          ? salutationOptions.filter((o) => o.value === field.value)
                          : []
                      }
                      onChange={(selected) => field.onChange(selected[0]?.value ?? '')}
                    />
                  )}
                />
              </FormRow>

              <FormRow
                label={t('editProfilePage.title_field')}
                validationMessage={errors.titleField?.message}
                variant={errors.titleField ? 'error' : undefined}
              >
                <Controller
                  name="titleField"
                  control={control}
                  render={({ field }) => (
                    <Select
                      data-testid="title-select"
                      disabled
                      options={titleOptions}
                      selectedOptions={
                        field.value
                          ? titleOptions.filter((o) => o.value === field.value)
                          : []
                      }
                      onChange={(selected) => field.onChange(selected[0]?.value ?? '')}
                    />
                  )}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.organisation')}>
                <Input
                  data-testid="organisation-input"
                  type="text"
                  disabled
                  style={{ width: '100%' }}
                  {...register('organisation')}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.firstName')}>
                <Input
                  data-testid="firstName-input"
                  type="text"
                  disabled
                  style={{ width: '100%' }}
                  {...register('firstName')}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.lastName')}>
                <Input
                  data-testid="lastName-input"
                  type="text"
                  disabled
                  style={{ width: '100%' }}
                  {...register('lastName')}
                />
              </FormRow>

              <Button
                data-testid="submit-button"
                type="button"
                variant="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={isPending || !isValid || !isDirty}
              >
                {isPending ? <Spinner size="sm" /> : t('editProfilePage.submit')}
              </Button>
            </Stack>
        </form>

        <Stack direction="column" spacing="s">
          <Button
            data-testid="delete-user-button"
            variant="primary-text"
            iconBefore="trashcan"
            onClick={() => {
              setIsModalVisible(true);
            }}
          >
            {t('editProfilePage.deleteUser')}
          </Button>
        </Stack>
      </Stack>
      {isModalVisible && <DeleteUsersAndRolesManagementDeleteDialog isOpen onClose={() => setIsModalVisible(false)} userId={userId ?? ''} />}
    </div>
  );
}
